import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, TrendingUp, IndianRupee, Download, BarChart3, RefreshCw, ChevronDown, ChevronRight, Users, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, LineChart, Line,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
const fmtK = (v) => {
  const n = Number(v || 0);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000)     return `₹${(n / 1000).toFixed(0)}K`;
  return fmt(n);
};

const BAR_COLORS = ["#047857","#059669","#10b981","#34d399","#6ee7b7","#a7f3d0","#d1fae5","#ecfdf5","#f0fdf4","#f7fce1"];

const DestinationReport = () => {
  const { token } = useAuth();
  const now = new Date();
  const currentFY = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const [selectedYear, setSelectedYear] = useState(currentFY.toString());
  const [data, setData] = useState([]);
  const [monthData, setMonthData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDest, setExpandedDest] = useState(null);
  const [drillData, setDrillData] = useState({});    // {destination: {rows, loading}}
  const years = Array.from({ length: 5 }, (_, i) => (currentFY - i).toString());

  const fyLabel = (y) => `FY ${y}-${String(Number(y) + 1).slice(2)}`;

  useEffect(() => { fetchData(); }, [selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    setExpandedDest(null);
    setDrillData({});
    try {
      const [destRes, monthRes] = await Promise.all([
        axios.get(`${API}/reports/destination-wise?year=${selectedYear}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/month-wise?year=${selectedYear}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setData(destRes.data?.data || []);
      // Build month trend array for the line chart
      const months = ["Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"];
      const raw = monthRes.data?.data || [];
      const monthMap = {};
      raw.forEach(m => { monthMap[`${m.year}-${String(m.month).padStart(2,'0')}`] = m; });
      const fyStart = Number(selectedYear);
      const trend = months.map((label, i) => {
        const yr = i < 9 ? fyStart : fyStart + 1;
        const mo = i < 9 ? i + 4 : i - 8;
        const key = `${yr}-${String(mo).padStart(2,'0')}`;
        const row = monthMap[key] || {};
        return { month: label, Revenue: row.revenue || 0, "Vendor Cost": row.vendor_paid || 0, Profit: row.net_profit || 0 };
      });
      setMonthData(trend);
    } catch {
      toast.error("Failed to load destination report");
    } finally {
      setLoading(false);
    }
  };

  const toggleDrillDown = async (dest) => {
    if (expandedDest === dest) { setExpandedDest(null); return; }
    setExpandedDest(dest);
    if (drillData[dest]) return; // already loaded
    setDrillData(prev => ({ ...prev, [dest]: { rows: [], loading: true } }));
    try {
      const res = await axios.get(`${API}/reports/destination-bookings?destination=${encodeURIComponent(dest)}&year=${selectedYear}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setDrillData(prev => ({ ...prev, [dest]: { rows: res.data?.data || [], loading: false } }));
    } catch {
      setDrillData(prev => ({ ...prev, [dest]: { rows: [], loading: false } }));
    }
  };

  // Totals
  const totals = data.reduce(
    (acc, row) => ({
      quotes: acc.quotes + (row.total_quotes || 0),
      bookings: acc.bookings + (row.total_bookings || 0),
      revenue: acc.revenue + (row.revenue || 0),
      vendor_paid: acc.vendor_paid + (row.vendor_paid || 0),
      profit: acc.profit + (row.profit || 0),
      pax: acc.pax + (row.total_pax || 0),
    }),
    { quotes: 0, bookings: 0, revenue: 0, vendor_paid: 0, profit: 0, pax: 0 }
  );
  const overallMargin = totals.revenue > 0 ? ((totals.profit / totals.revenue) * 100).toFixed(1) : "0.0";

  // Export CSV
  const exportCSV = () => {
    if (!data.length) return;
    const headers = ["Destination","Quotes","Bookings","Conv%","Pax","Revenue","Vendor Cost","Gross Profit","Margin%"];
    const rows = data.map(r => [
      r.destination, r.total_quotes, r.total_bookings, r.conversion_rate,
      r.total_pax, r.revenue, r.vendor_paid, r.profit, r.margin_pct,
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `Destination_Report_${fyLabel(selectedYear).replace(" ","_")}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const chartData = data.slice(0, 10).map(r => ({
    name: r.destination.length > 12 ? r.destination.slice(0, 12) + "…" : r.destination,
    Revenue: r.revenue,
    "Vendor Cost": r.vendor_paid,
    Profit: r.profit,
  }));

  return (
    <DashboardLayout type="admin">
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <MapPin className="w-6 h-6 text-emerald-600" />
              Destination Wise Report
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">P&amp;L breakdown grouped by destination — {fyLabel(selectedYear)}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* FY Selector */}
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(e.target.value)}
              className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
              data-testid="dest-fy-select"
            >
              {years.map(y => (
                <option key={y} value={y}>{fyLabel(y)}</option>
              ))}
            </select>
            <Button variant="outline" size="sm" onClick={fetchData} className="gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportCSV} disabled={!data.length} className="gap-1.5">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Destinations", value: data.length, icon: MapPin, color: "text-emerald-600", bg: "bg-emerald-50" },
            { label: "Total Revenue", value: fmtK(totals.revenue), icon: IndianRupee, color: "text-blue-600", bg: "bg-blue-50" },
            { label: "Total Cost", value: fmtK(totals.vendor_paid), icon: TrendingUp, color: "text-rose-600", bg: "bg-rose-50" },
            { label: `Net Profit (${overallMargin}%)`, value: fmtK(totals.profit), icon: BarChart3, color: "text-amber-600", bg: "bg-amber-50" },
          ].map((s, i) => (
            <Card key={i} className="bg-white border-gray-200 shadow-sm">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center shrink-0`}>
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 truncate">{s.label}</p>
                  <p className="text-lg font-bold text-gray-900 leading-tight">{s.value}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bar Chart — top 10 destinations */}
        {!loading && data.length > 0 && (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm font-semibold text-gray-700 mb-4">Top 10 Destinations — Revenue vs Cost vs Profit</p>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6b7280" }} />
                    <YAxis tickFormatter={v => fmtK(v)} tick={{ fontSize: 11, fill: "#6b7280" }} />
                    <Tooltip formatter={(val, name) => [fmt(val), name]} contentStyle={{ backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Revenue" fill="#047857" radius={[3,3,0,0]} />
                    <Bar dataKey="Vendor Cost" fill="#f87171" radius={[3,3,0,0]} />
                    <Bar dataKey="Profit" fill="#f59e0b" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Monthly Trend Line Chart */}
        {!loading && monthData.some(m => m.Revenue > 0) && (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm font-semibold text-gray-700 mb-4">Monthly Revenue Trend — {fyLabel(selectedYear)}</p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthData} margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6b7280" }} />
                    <YAxis tickFormatter={v => fmtK(v)} tick={{ fontSize: 11, fill: "#6b7280" }} />
                    <Tooltip formatter={(val, name) => [fmt(val), name]} contentStyle={{ backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="Revenue" stroke="#047857" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="Vendor Cost" stroke="#f87171" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="Profit" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="4 2" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Data Table */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
              </div>
            ) : data.length === 0 ? (
              <div className="text-center py-16">
                <MapPin className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400">No data for {fyLabel(selectedYear)}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="destination-report-table">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/70">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Destination</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Quotes</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Bookings</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Conv %</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Pax</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Revenue</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Vendor Cost</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Gross Profit</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Margin</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data.map((row, i) => (
                      <>
                        <tr
                          key={`row-${i}`}
                          className="hover:bg-emerald-50/30 transition-colors cursor-pointer"
                          onClick={() => toggleDrillDown(row.destination)}
                          data-testid={`dest-row-${i}`}
                        >
                          <td className="px-4 py-3 text-xs text-gray-400">{i + 1}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {expandedDest === row.destination
                                ? <ChevronDown className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                : <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              }
                              <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: BAR_COLORS[i % BAR_COLORS.length] }} />
                              <span className="font-semibold text-gray-800 text-sm">{row.destination}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-gray-600">{row.total_quotes}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-600">{row.total_bookings}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={`inline-block text-xs font-semibold px-1.5 py-0.5 rounded-full ${row.conversion_rate >= 50 ? "bg-emerald-100 text-emerald-700" : row.conversion_rate >= 25 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"}`}>{row.conversion_rate}%</span>
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-gray-600">{row.total_pax}</td>
                          <td className="px-4 py-3 text-right text-sm font-semibold text-gray-800">{fmt(row.revenue)}</td>
                          <td className="px-4 py-3 text-right text-sm text-rose-600">{fmt(row.vendor_paid)}</td>
                          <td className="px-4 py-3 text-right text-sm font-semibold text-emerald-700">{fmt(row.profit)}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={`inline-block text-xs font-semibold px-1.5 py-0.5 rounded-full ${row.margin_pct >= 20 ? "bg-emerald-100 text-emerald-700" : row.margin_pct >= 10 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{row.margin_pct}%</span>
                          </td>
                        </tr>
                        {/* Drill-down sub-table */}
                        {expandedDest === row.destination && (
                          <tr key={`drill-${i}`}>
                            <td colSpan={10} className="px-0 py-0 bg-emerald-50/20">
                              {drillData[row.destination]?.loading ? (
                                <div className="flex justify-center py-4">
                                  <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
                                </div>
                              ) : (drillData[row.destination]?.rows || []).length === 0 ? (
                                <div className="text-center py-3 text-xs text-gray-400">No bookings found for this destination</div>
                              ) : (
                                <table className="w-full">
                                  <thead>
                                    <tr className="bg-emerald-100/40 border-y border-emerald-100">
                                      <th className="pl-14 pr-4 py-2 text-left text-xs font-semibold text-emerald-700">Booking ID</th>
                                      <th className="px-4 py-2 text-left text-xs font-semibold text-emerald-700">Agent</th>
                                      <th className="px-4 py-2 text-left text-xs font-semibold text-emerald-700">Guest</th>
                                      <th className="px-4 py-2 text-right text-xs font-semibold text-emerald-700">Pax</th>
                                      <th className="px-4 py-2 text-left text-xs font-semibold text-emerald-700">Travel Date</th>
                                      <th className="px-4 py-2 text-right text-xs font-semibold text-emerald-700">Revenue</th>
                                      <th className="px-4 py-2 text-center text-xs font-semibold text-emerald-700">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-emerald-50">
                                    {(drillData[row.destination]?.rows || []).map((bk, bi) => (
                                      <tr key={bi} className="hover:bg-emerald-50/40">
                                        <td className="pl-14 pr-4 py-2 font-mono text-xs text-gray-500">{bk.booking_ref}</td>
                                        <td className="px-4 py-2 text-xs text-gray-700">{bk.agent_company || bk.agent_name}</td>
                                        <td className="px-4 py-2 text-xs text-gray-700">{bk.guest_name}</td>
                                        <td className="px-4 py-2 text-xs text-right text-gray-600">{bk.pax}</td>
                                        <td className="px-4 py-2 text-xs text-gray-600">{bk.travel_date ? new Date(bk.travel_date).toLocaleDateString('en-IN', {day:'2-digit', month:'short', year:'numeric'}) : '—'}</td>
                                        <td className="px-4 py-2 text-xs text-right font-semibold text-gray-800">{fmt(bk.total_amount)}</td>
                                        <td className="px-4 py-2 text-center">
                                          <span className={`inline-block text-xs px-1.5 py-0.5 rounded-full font-medium ${bk.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : bk.status === 'cancelled' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>{bk.status}</span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                    {/* Totals row */}
                    <tr className="bg-emerald-50/60 border-t-2 border-emerald-200">
                      <td className="px-4 py-3" colSpan={2}>
                        <span className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Total — {data.length} Destinations</span>
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-gray-700">{totals.quotes}</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-gray-700">{totals.bookings}</td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-xs font-bold text-gray-700">
                          {totals.quotes > 0 ? Math.round((totals.bookings / totals.quotes) * 100) : 0}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-gray-700">{totals.pax}</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-gray-800">{fmt(totals.revenue)}</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-rose-700">{fmt(totals.vendor_paid)}</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-emerald-800">{fmt(totals.profit)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-xs font-bold text-emerald-800">{overallMargin}%</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default DestinationReport;
